import {
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import {
  ListCifCupsFormatted,
  ManagementUpdateCup,
} from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import theme from "../../../muiTheme";
import EnvirormentContent from "../../EnvirormentContent/EnvirormentContent";
import { RefObject, memo } from "react";
import "./CupsCifSelectorTables.scss";

export interface CupsCifSelectorTableProps {
  bgDark?: boolean;
  isManagementsComponent: boolean;
  selectedCups: ManagementUpdateCup[];
  listCupsCifFiltered: ListCifCupsFormatted[];
  searchedValue: string;
  setShowAlertError: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  tableElementRef?: RefObject<HTMLDivElement>;
  lastElementRef: (divElement: HTMLDivElement) => void;
  isCupSelected: (cupsId: string) => boolean;
  handleChangeSelectAllChechbox: (
    event: React.ChangeEvent<HTMLInputElement>,
    cups: ManagementUpdateCup[],
    cif: string
  ) => void;
  handleChangeCheckbox: (
    event: React.ChangeEvent<HTMLInputElement>,
    cup: ManagementUpdateCup
  ) => void;
}

const CupsCifSelectorTables = ({
  bgDark,
  isManagementsComponent,
  isCupSelected,
  listCupsCifFiltered,
  selectedCups,
  lastElementRef,
  handleChangeSelectAllChechbox,
  handleChangeCheckbox,
  setShowAlertError,
  tableElementRef,
  
}: CupsCifSelectorTableProps) => {
  const isCupSelectedInCif = (cups: ManagementUpdateCup[]) =>
    cups.some(({ cupsContractId }) => isCupSelected(cupsContractId));
  const listCupsCifFilteredUnique = listCupsCifFiltered.map((element) => ({
    ...element,
    cups: element.cups.reduce(
      (accumulator: ManagementUpdateCup[], current: ManagementUpdateCup) => {
        if (!accumulator.find((item) => item.cupsId === current.cupsId)) {
          accumulator.push(current);
        }
        return accumulator;
      },
      []
    ),
  }));

  const getBackgroundRowColor = (isCupInCif: boolean, isDisabled: boolean) => {
    switch (true) {
      case isCupInCif:
        return {
          backgroundColor: theme.palette.selected.light,
          borderColor: theme.palette.selected.main,
        };
      case isDisabled:
        return {
          backgroundColor: theme.palette.grey[100],
          borderColor: theme.palette.grey[300],
        };
      default:
        return {
          backgroundColor: theme.palette.background.default,
          borderColor: theme.palette.grey[100],
        };
    }
  };  

  return (
    <Stack
      component="div"
      className={`cups-cif-selector-tables ${isManagementsComponent || bgDark ? "bottom-line" : ""}`}
      ref={tableElementRef}
      spacing={2}
      bgcolor={bgDark ? theme.palette.grey[100] : "auto"}
      maxHeight={{xs:"422px", md: "688px"}}
      overflow={"auto"}
    >
      {listCupsCifFilteredUnique.map(
        ({ id, cups, cif, customerDescription }) => {
          const isCupInCif = isCupSelectedInCif(cups);
          const isDisabled =
            !isCupInCif && !!selectedCups.length && isManagementsComponent;
          const { backgroundColor, borderColor } = getBackgroundRowColor(
            isCupInCif,
            isDisabled
          );
          return (
            <TableContainer
              ref={lastElementRef}
              key={id}
              sx={{ overflow: "visible" }}
            >
              <Table
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableHead
                  sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}` }}
                >
                  <TableRow
                    onClick={() => isDisabled && setShowAlertError(true)}
                    sx={{
                      boxShadow: "unset",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                      margin: 0,
                      paddingY: 1,
                      paddingX: 4,
                      gap: { xs: 2, lg: 3 },
                      backgroundColor,
                      borderColor,
                    }}
                  >
                    <TableCell sx={{ padding: 0 }}>
                      <Checkbox
                        checked={isCupInCif}
                        indeterminate={isCupInCif}
                        onChange={(event) =>
                          handleChangeSelectAllChechbox(event, cups, cif)
                        }
                        icon={<span className="t-icon-checkbox-default" />}
                        indeterminateIcon={
                          <span
                            className={`t-icon t-icon-checkbox-ripple ${
                              true ? "indeterminate" : ""
                            }`}
                          />
                        }
                        disabled={isDisabled}
                        sx={{ fontSize: "24px" }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: 0 }}>
                      <Typography
                        component="p"
                        variant="body1"
                        fontWeight={600}
                      >
                        {customerDescription}
                      </Typography>
                      <Typography
                        component="p"
                        variant="body2"
                        color={theme.palette.grey[500]}
                      >{`CIF ${cif}`}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cups.map((cup, indexNumber) => {
                    const {
                      supplyAddress,
                      cupsCode,
                      cupsId,
                      cupsContractId,
                      envirorment,
                      cupsAssociatedCif,
                    } = cup;
                    const isLastRow = cups.length - 1 === indexNumber;
                    const isSelected = isCupSelected(cupsContractId);
                    const { backgroundColor, borderColor } =
                      getBackgroundRowColor(isSelected, isDisabled);
                    return (
                      <TableRow
                        key={`${cupsId}_${cupsAssociatedCif}_$${indexNumber}`}
                        onClick={() => isDisabled && setShowAlertError(true)}
                        sx={{
                          boxShadow: "unset",
                          borderRadius: 0,
                          borderBottomLeftRadius: (isLastRow && "8px") || 0,
                          borderBottomRightRadius: (isLastRow && "8px") || 0,
                          margin: 0,
                          paddingY: 1,
                          paddingX: 4,
                          gap: { xs: 2, lg: 3 },
                          backgroundColor,
                          borderTop: `1px solid ${
                            isSelected && !!indexNumber
                              ? borderColor
                              : theme.palette.grey[100]
                          }`,
                        }}
                      >
                        <TableCell sx={{ padding: 0 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={(event) => {
                              handleChangeCheckbox(event, cup);
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            icon={<span className="t-icon-checkbox-default" />}
                            checkedIcon={
                              <span className="t-icon t-icon-checkbox-checked" />
                            }
                            disabled={isDisabled}
                            sx={{ fontSize: "24px" }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: 0 }}>
                          <Typography component="p" variant="body2">
                            {supplyAddress}
                          </Typography>
                          <Typography
                            component="p"
                            variant="body2"
                            sx={{ color: theme.palette.grey[500] }}
                          >
                            <EnvirormentContent
                              service={envirorment}
                              variant={"body2"}
                              showServiceText={false}
                            />{" "}
                            {`${cupsCode}`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }
      )}
    </Stack>
  );
};

export default memo(
  CupsCifSelectorTables,
  (previousProps, nextProps) =>
    previousProps.searchedValue === nextProps.searchedValue &&
    previousProps.selectedCups === nextProps.selectedCups &&
    previousProps.page === nextProps.page
);
